import type { ForwardedRef } from 'react';
import type { Nullable } from '@ricoh/ria.client.core/types/core/data.type';

import { useImperativeHandle, useMemo } from 'react';
import { ICON_SIZE } from '@ricoh/ria.client.core/constants/component/icon';

import type { AppIconsProps, FontawesomeProps, IconHooks, IconProps, IconRef, IconRenderingProps, OctIconProps } from './_.type';
import type { AppIconsMap, FontawesomeMap, OcticonsMap } from './map';

import { appIconsMap, defaultGoogleIconFontVariation, fontawesomeMap, googleIconsMap, octiconsMap } from './map';

/**
 * Create App Icons props.
 *
 * @param {string} name Icon nmae.
 * @param {boolean} fill Is fill icon.
 *
 * @returns {Nullable<AppIconsProps>} App Icons props.
 */
function createAppIconsProps(name: string, fill: boolean): Nullable<AppIconsProps> {
  if (!fill && Object.keys<keyof AppIconsMap['outline']>(appIconsMap.outline).includes(name)) {
    return {
      $is: 'app',
      icon: appIconsMap.outline[name],
    };
  }

  if (fill && Object.keys<keyof AppIconsMap['fill']>(appIconsMap.fill).includes(name)) {
    return {
      $is: 'app',
      icon: appIconsMap.fill[name],
    };
  }

  return undefined;
}

/**
 * Create OctIcons props.
 *
 * @param {string} name Icon nmae.
 * @param {boolean} fill Is fill icon.
 * @param {number} size Icon size.
 *
 * @returns {Nullable<OctIconProps>} OctIcons props.
 */
function createOctIconsProps(name: string, fill: boolean, size: number): Nullable<OctIconProps> {
  if (!fill && Object.keys<keyof OcticonsMap['outline']>(octiconsMap.outline).includes(name)) {
    return {
      $is: 'oct',
      icon: octiconsMap.outline[name],
      size,
      verticalAlign: 'middle',
    };
  }

  if (fill && Object.keys<keyof OcticonsMap['fill']>(octiconsMap.fill).includes(name)) {
    return {
      $is: 'oct',
      icon: octiconsMap.fill[name],
      size,
      verticalAlign: 'middle',
    };
  }

  return undefined;
}

/**
 * Create Fontawesome props.
 *
 * @param {string} name Icon nmae.
 * @param {boolean} fill Is fill icon.
 * @param {number} size Icon size.
 *
 * @returns {Nullable<FontawesomeProps>} Fontawesome props.
 */
function createFontawesomeProps(name: string, fill: boolean, size: number): Nullable<FontawesomeProps> {
  if (!fill && Object.keys<keyof FontawesomeMap['outline']>(fontawesomeMap.outline).includes(name)) {
    return {
      $is: 'fontawesome',
      icon: fontawesomeMap.outline[name],
      fontSize: size,
    };
  }

  if (fill && Object.keys<keyof FontawesomeMap['fill']>(fontawesomeMap.fill).includes(name)) {
    return {
      $is: 'fontawesome',
      icon: fontawesomeMap.fill[name],
      fontSize: size,
    };
  }

  return undefined;
}

/**
 * Use Icon hooks.
 *
 * @param {IconProps} props Icon props.
 * @param {IconRef} ref Icon ref.
 *
 * @returns {IconHooks} Icon hooks.
 */
export default function useIconHooks({ name, size = 'md', fill = false }: IconProps, ref: ForwardedRef<IconRef>): IconHooks {
  const iconSize = useMemo<number>(() => {
    if (typeof size === 'number') {
      return size;
    }

    return ICON_SIZE[size.toUpperCase() as keyof typeof ICON_SIZE] ?? ICON_SIZE.MD;
  }, [size]);

  const iconRenderingProps = useMemo<IconRenderingProps>(() => {
    let props;

    props = createAppIconsProps(name, fill);
    if (props) {
      return props;
    }

    props = createFontawesomeProps(name, fill, iconSize);
    if (props) {
      return props;
    }

    props = createOctIconsProps(name, fill, iconSize);
    if (props) {
      return props;
    }

    const fontVariationSettings = googleIconsMap[name] ?? defaultGoogleIconFontVariation;
    return {
      $is: 'google',
      name,
      style: {
        fontVariationSettings: fontVariationSettings({ fill, size }),
        fontSize: `${iconSize}px`,
      },
    };
  }, [fill, iconSize, name, size]);

  useImperativeHandle(ref, () => ({}));

  return useMemo<IconHooks>(
    () => ({
      refs: {},

      states: {
        iconRenderingProps,
        iconSize,
      },

      events: {},
    }),
    [iconRenderingProps, iconSize],
  );
}
